import { Box, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import FormSchemaParser from 'components/Common/FormSchemaParser';
import { TelephonyField, NetsapiensCsp } from 'apis/agency/telephony/types';
import Button from 'components/Common/Button';
import { Control, useWatch } from 'react-hook-form';
import CustomizeTelephonySection from './CustomizeTelephonySection';
import NetsapiensCspSection from './NetsapiensCspSection';
import WebhookConfigInfo from './WebhookConfigInfo';
import { mapTelephonyFieldsToFormSchema } from '../utils/formFields';
import { getTelephonyFormSections } from '../utils/formSections';

import { SupportedProvider } from '../utils/types';

interface Props {
  control: Control<any>;
  fields: TelephonyField[];
  onReset: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  logoUrl?: string;
  onLogoUpload: (file: File) => Promise<void>;
  isUploadingLogo?: boolean;
  isPending?: boolean;
  provider: SupportedProvider;
  netsapiensInitialCsps?: NetsapiensCsp[];
  onPersistNetsapiensCsps?: (csps: NetsapiensCsp[]) => Promise<void>;
}

const getHeaderContent = (provider: SupportedProvider) => {
  if (provider === 'twilio') {
    return {
      title: 'Configuration',
      description: 'Connect your Twilio account so customers can buy and manage phone numbers directly within platform.'
    };
  }
  if (provider === 'netsapiens') {
    return {
      title: 'Configuration',
      description: 'Connect your Netsapiens instance to deploy and manage AI agents using SIP trunking.'
    };
  }
  if (provider === 'asterisk') {
    return {
      title: 'Configuration',
      description: 'Securely connect your telephony service using the credentials and SIP configuration below.'
    };
  }
  if (provider === 'bandwidth') {
    return {
      title: 'Configuration',
      description:
        'Connect to Bandwidth to provision phone numbers for agents within the platform. Bandwidth will also be used for SMS hosting and messaging services.'
    };
  }
  return {
    title: 'Configuration',
    description: 'Securely connect your telephony service using the credentials and SIP configuration below.'
  };
};

export default function TelephonyApiKeyForm({
  control,
  fields,
  onReset,
  onSubmit,
  logoUrl,
  onLogoUpload,
  isUploadingLogo = false,
  provider,
  netsapiensInitialCsps = [],
  onPersistNetsapiensCsps = async () => undefined,
  isPending = false
}: Props) {
  const theme = useTheme();
  const formFields = mapTelephonyFieldsToFormSchema(fields);
  const isActive = useWatch({
    control,
    name: 'active'
  });

  const header = getHeaderContent(provider);
  const isNetsapiens = provider === 'netsapiens';
  const sections = getTelephonyFormSections(provider);
  const fieldsByKey = new Map(formFields.map((field) => [field.key, field]));

  const renderSectionFields = (fieldKeys: string[], webhookInfoBeforeKey?: string) => {
    const sectionFields = fieldKeys.map((key) => fieldsByKey.get(key)).filter(Boolean) as typeof formFields;

    if (!sectionFields.length) return null;

    if (!webhookInfoBeforeKey) {
      return <FormSchemaParser fields={sectionFields} control={control} namePrefix='configuration' gridSize={12} />;
    }

    const webhookIndex = sectionFields.findIndex((field) => field.key === webhookInfoBeforeKey);
    if (webhookIndex < 0) {
      return <FormSchemaParser fields={sectionFields} control={control} namePrefix='configuration' gridSize={12} />;
    }

    const fieldsBeforeWebhook = sectionFields.slice(0, webhookIndex);
    const fieldsFromWebhook = sectionFields.slice(webhookIndex);

    return (
      <>
        {fieldsBeforeWebhook.length > 0 && (
          <FormSchemaParser fields={fieldsBeforeWebhook} control={control} namePrefix='configuration' gridSize={12} />
        )}
        <WebhookConfigInfo provider={provider} />
        {fieldsFromWebhook.length > 0 && (
          <FormSchemaParser fields={fieldsFromWebhook} control={control} namePrefix='configuration' gridSize={12} />
        )}
      </>
    );
  };

  const renderFormFields = () => {
    if (!formFields.length) return null;

    if (sections) {
      const renderedKeys = new Set(sections.flatMap((section) => section.fieldKeys));
      const leftoverFields = formFields.filter((field) => !renderedKeys.has(field.key));

      return (
        <Stack direction='column' spacing={3} width='100%'>
          {sections.map((section) => {
            const hasFields = section.fieldKeys.some((key) => fieldsByKey.has(key));
            if (!hasFields && !section.webhookInfoBeforeKey) return null;

            return (
              <Box key={section.title} width='100%'>
                <Typography fontWeight={500} fontSize={16} mb={0.5}>
                  {section.title}
                </Typography>
                <Typography color='secondary.main' fontSize={14} mb={2}>
                  {section.description}
                </Typography>
                {renderSectionFields(section.fieldKeys, section.webhookInfoBeforeKey)}
              </Box>
            );
          })}
          {leftoverFields.length > 0 && (
            <FormSchemaParser fields={leftoverFields} control={control} namePrefix='configuration' gridSize={12} />
          )}
        </Stack>
      );
    }

    return <FormSchemaParser fields={formFields} control={control} namePrefix='configuration' gridSize={12} />;
  };

  return (
    <Box component='form' onSubmit={onSubmit}>
      {isNetsapiens ? (
        <NetsapiensCspSection
          initialCsps={netsapiensInitialCsps}
          onPersistCsps={onPersistNetsapiensCsps}
          title={header.title}
          description={header.description}
        />
      ) : (
        <Grid
          container
          spacing={1.8}
          bgcolor={theme.palette.background.paper}
          padding={{ xs: 2, lg: 3 }}
          borderRadius='4px'
          border='1px solid #ECECEC'
        >
          {!sections && (
            <Stack direction='column' width='100%'>
              <Typography fontWeight={500} fontSize={16}>
                {header.title}
              </Typography>
              <Typography color='secondary.main' fontSize={14}>
                {header.description}
              </Typography>
            </Stack>
          )}

          {formFields.length > 0 && (
            <Grid size={12} width='100%'>
              {renderFormFields()}
            </Grid>
          )}
        </Grid>
      )}

      <CustomizeTelephonySection
        control={control}
        logoUrl={logoUrl}
        onLogoUpload={onLogoUpload}
        isUploadingLogo={isUploadingLogo}
      />
      <Box mt={2} display='flex' flexDirection={{ xs: 'column-reverse', sm: 'row' }} gap={1.2} justifyContent='end'>
        <Button
          color='secondary'
          variant='outlined'
          sx={{
            border: '1px solid #ECECEC',
            bgcolor: 'white',
            width: { xs: '100%', sm: 'auto' },
            borderRadius: '18px',
            px: 2.5
          }}
          onClick={onReset}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='contained'
          disabled={!isActive}
          isPending={isPending}
          color='primary'
          sx={{ width: { xs: '100%', sm: 'auto' }, borderRadius: '18px', px: 2.5 }}
        >
          Configure
        </Button>
      </Box>
    </Box>
  );
}
